import { useEffect } from 'react';
import type { TaskPriority } from '../types';

export interface TaskKeyboardActions {
  next: () => void;
  prev: () => void;
  open?: () => void;
  close?: () => void;
  complete?: () => void;
  togglePin?: () => void;
  snooze?: () => void;
  addNote?: () => void;
  setPriority?: (priority: TaskPriority) => void;
  /**
   * Move the selected task to another group. Receives the destination
   * teamId. Keyboard shortcut `m <key>` only supports the four legacy
   * partitions below; any group outside that set must be moved via the
   * Move dialog.
   */
  move?: (toTeamId: string) => void;
  toggleHelp?: () => void;
}

const MOVE_KEYS: Record<string, string> = {
  p: 'personal',
  w: 'woxom-health',
  l: 'leadmomentum',
  c: 'client-link',
};

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
}

export function useTaskKeyboard(actions: TaskKeyboardActions, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    let awaitingMove = false;
    let moveTimer: ReturnType<typeof setTimeout> | null = null;

    const handler = (e: KeyboardEvent) => {
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (awaitingMove) {
        const target = MOVE_KEYS[e.key.toLowerCase()];
        if (target && actions.move) {
          e.preventDefault();
          actions.move(target);
        }
        awaitingMove = false;
        if (moveTimer) clearTimeout(moveTimer);
        return;
      }

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          actions.next();
          break;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          actions.prev();
          break;
        case 'Enter':
          if (actions.open) {
            e.preventDefault();
            actions.open();
          }
          break;
        case 'Escape':
          if (actions.close) {
            e.preventDefault();
            actions.close();
          }
          break;
        case ' ':
          if (actions.complete) {
            e.preventDefault();
            actions.complete();
          }
          break;
        case 'p':
          if (actions.togglePin) {
            e.preventDefault();
            actions.togglePin();
          }
          break;
        case 's':
          if (actions.snooze) {
            e.preventDefault();
            actions.snooze();
          }
          break;
        case 'n':
          if (actions.addNote) {
            e.preventDefault();
            actions.addNote();
          }
          break;
        case '1':
          actions.setPriority?.('low');
          break;
        case '2':
          actions.setPriority?.('medium');
          break;
        case '3':
          actions.setPriority?.('high');
          break;
        case 'm':
          if (actions.move) {
            e.preventDefault();
            awaitingMove = true;
            moveTimer = setTimeout(() => {
              awaitingMove = false;
            }, 3000);
          }
          break;
        case '?':
          if (actions.toggleHelp) {
            e.preventDefault();
            actions.toggleHelp();
          }
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (moveTimer) clearTimeout(moveTimer);
    };
  }, [actions, enabled]);
}
